// VideoLobby.jsx — Public join page mounted at /join/:slug
//
// Flow:
//   1. Read slug from URL → GET /api/video-rooms/:slug for room info (public).
//   2. Show camera/mic preview + display name input ("lobby" state).
//   3. On Join:
//        POST /api/video-rooms/:slug/join { displayName }
//          → { token, telnyxRoomId, slug, title }
//        initialize({ roomId: telnyxRoomId, clientToken: token })
//        addStream('self', {...localTracks}); render grid.
//   4. Same Telnyx event wiring as VideoCall.jsx (participant_joined,
//      stream_published+addSubscription, subscription_started→getParticipantStream,
//      track_enabled/disabled, disconnected) so participant labels and tile
//      lifecycle behave identically.
//
// No auth required. No socket.io presence (public visitors aren't in the
// tenant presence map). Display name is encoded into Telnyx participant
// context by the backend so other peers see it on this user's tile.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { initialize } from '@telnyx/video';

const API_URL = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/api`
  : '/api';

// ── Tiny SVG icons ──────────────────────────────────────────────────────────
function Ico({ children, className = 'h-5 w-5' }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
      className={className} aria-hidden="true">{children}</svg>
  );
}
const VideoIcon    = ({ className }) => <Ico className={className}><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></Ico>;
const VideoOffIcon = ({ className }) => <Ico className={className}><line x1="1" y1="1" x2="23" y2="23"/><path d="M15 11.08V11a4 4 0 0 0-5.93-3.5"/><path d="M9.1 9.09A4 4 0 0 0 9 10v1H5"/><path d="M16 19H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h3l14 14"/><path d="M23 7l-7 5 7 5V7z"/></Ico>;
const MicIcon      = ({ className }) => <Ico className={className}><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></Ico>;
const MicOffIcon   = ({ className }) => <Ico className={className}><line x1="1" y1="1" x2="23" y2="23"/><path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"/><path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></Ico>;
const PhoneOffIcon = ({ className }) => <Ico className={className}><path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.42 19.42 0 0 1 3.43 9.37"/><line x1="23" y1="1" x2="1" y2="23"/></Ico>;

// ── Avatar fallback (when video off) ────────────────────────────────────────
function Avatar({ label }) {
  const initials = label
    ? label.split(/[@.\s]/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('')
    : '?';
  return (
    <div className="h-16 w-16 rounded-full flex items-center justify-center text-xl font-bold bg-sky-500/20 text-sky-300 select-none">
      {initials}
    </div>
  );
}

// ── Grid sizing ─────────────────────────────────────────────────────────────
function gridClasses(count) {
  if (count <= 1) return 'grid-cols-1';
  if (count <= 2) return 'grid-cols-1 md:grid-cols-2';
  if (count <= 4) return 'grid-cols-2';
  if (count <= 6) return 'grid-cols-2 md:grid-cols-3';
  return 'grid-cols-3 md:grid-cols-4';
}

// ── ParticipantTile ─────────────────────────────────────────────────────────
function ParticipantTile({ id, label, mediaStream, hasVideo, isLocal }) {
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  useEffect(() => {
    if (videoRef.current) videoRef.current.srcObject = mediaStream || null;
    if (audioRef.current && !isLocal) audioRef.current.srcObject = mediaStream || null;
  }, [mediaStream, isLocal]);
  return (
    <div className="relative rounded-2xl overflow-hidden border bg-black border-white/10 aspect-video">
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted={isLocal}
        className="w-full h-full object-cover"
        style={isLocal ? { transform: 'scaleX(-1)' } : undefined}
      />
      {!isLocal && <audio ref={audioRef} autoPlay playsInline />}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        {(!hasVideo || !mediaStream) && <Avatar label={label} />}
      </div>
      <div className="absolute bottom-2 left-2 px-2.5 py-1 rounded-lg bg-black/60 backdrop-blur text-white text-xs font-medium">
        {label}{isLocal ? ' (You)' : ''}
      </div>
      <span className="sr-only" data-participant-id={id} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function VideoLobby() {
  const { slug } = useParams();

  // 'loading' | 'lobby' | 'joining' | 'connected' | 'ended' | 'error'
  const [phase, setPhase] = useState('loading');
  const [roomInfo, setRoomInfo] = useState(null);
  const [error, setError] = useState('');
  const [displayName, setDisplayName] = useState('');

  // Lobby preview + in-call state
  const [previewStream, setPreviewStream] = useState(null);
  const [audioMuted, setAudioMuted] = useState(false);
  const [videoOff, setVideoOff] = useState(false);
  const [remotes, setRemotes] = useState({}); // { id: { id,label,mediaStream,hasVideo } }

  const previewVideoRef = useRef(null);
  const previewStreamRef = useRef(null);
  const roomRef = useRef(null);
  const callStreamRef = useRef(null);
  const localSelfRef = useRef(null);
  const localPidRef = useRef(null); // Telnyx-assigned local participant ID — used to skip self echoes
  const subscribedRef = useRef(new Set()); // `${pid}|${key}` pairs we've already subscribed to (or attempted)

  // ── 1. Fetch room info ────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/video-rooms/${slug}`);
        if (cancelled) return;
        setRoomInfo(data);
        setPhase('lobby');
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.message || 'Could not find this room.');
        setPhase('error');
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  // ── 2. Acquire camera/mic for lobby preview ───────────────────────────────
  useEffect(() => {
    if (phase !== 'lobby') return undefined;
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        previewStreamRef.current = stream;
        setPreviewStream(stream);
      } catch (e) {
        setError(`Camera/mic access denied: ${e?.message || e}`);
      }
    })();
    return () => {
      cancelled = true;
      // Don't stop preview tracks here — we hand them off to the call below.
    };
  }, [phase]);

  // Bind preview stream to <video>.
  useEffect(() => {
    if (previewVideoRef.current) {
      previewVideoRef.current.srcObject = previewStream || null;
    }
  }, [previewStream, phase]);

  // ── Lobby controls ────────────────────────────────────────────────────────
  const togglePreviewAudio = () => {
    const s = previewStreamRef.current;
    if (!s) return;
    const next = audioMuted; // currently muted → enable
    s.getAudioTracks().forEach((t) => { t.enabled = next; });
    setAudioMuted(!next);
  };
  const togglePreviewVideo = () => {
    const s = previewStreamRef.current;
    if (!s) return;
    const next = videoOff;
    s.getVideoTracks().forEach((t) => { t.enabled = next; });
    setVideoOff(!next);
  };

  // ── 3. Leave / cleanup ────────────────────────────────────────────────────
  const leaveRoom = useCallback(() => {
    const room = roomRef.current;
    roomRef.current = null;
    if (room) {
      try { room.removeAllListeners?.(); } catch { /* ignore */ }
      try {
        const p = room.disconnect?.();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      } catch { /* ignore */ }
    }
    if (callStreamRef.current) {
      try {
        callStreamRef.current.getTracks().forEach((t) => {
          try { t.stop(); } catch { /* ignore */ }
        });
      } catch { /* ignore */ }
      callStreamRef.current = null;
    }
    if (previewStreamRef.current) {
      try {
        previewStreamRef.current.getTracks().forEach((t) => {
          try { t.stop(); } catch { /* ignore */ }
        });
      } catch { /* ignore */ }
      previewStreamRef.current = null;
    }
    localSelfRef.current = null;
    localPidRef.current = null;
    subscribedRef.current = new Set();
    setRemotes({});
    setPreviewStream(null);
    setPhase('ended');
  }, []);

  // Unmount safety
  useEffect(() => {
    return () => {
      if (roomRef.current) { try { roomRef.current.disconnect?.(); } catch {} }
      if (callStreamRef.current) {
        callStreamRef.current.getTracks().forEach((t) => { try { t.stop(); } catch {} });
      }
      if (previewStreamRef.current) {
        previewStreamRef.current.getTracks().forEach((t) => { try { t.stop(); } catch {} });
      }
    };
  }, []);

  // ── 4. Join action — mint token, init Telnyx, publish local tracks ────────
  const handleJoin = useCallback(async () => {
    setError('');
    const name = displayName.trim() || 'Guest';

    // Reuse the lobby's media stream so the call starts instantly with the
    // exact device the user just previewed (no second permission prompt).
    const localStream = previewStreamRef.current;
    if (!localStream) {
      setError('No camera/mic stream — refresh and try again.');
      return;
    }
    callStreamRef.current = localStream;
    previewStreamRef.current = null; // ownership transferred to the call

    setPhase('joining');
    try {
      const { data } = await axios.post(`${API_URL}/video-rooms/${slug}/join`, {
        displayName: name,
      });
      const { token, telnyxRoomId } = data;
      if (!telnyxRoomId) throw new Error('Server did not return telnyxRoomId');
      // eslint-disable-next-line no-console
      console.log('[lobby join] telnyxRoomId:', telnyxRoomId, 'tokenLen:', token?.length);

      const room = await initialize({
        roomId: telnyxRoomId,
        clientToken: token,
        enableMessages: false,
      });
      roomRef.current = room;
      // eslint-disable-next-line no-console
      console.log('[lobby room] keys:', Object.keys(room || {}));
      // eslint-disable-next-line no-console
      console.log('[lobby room] proto:', room && Object.getOwnPropertyNames(Object.getPrototypeOf(room) || {}));
      try {
        if (typeof window !== 'undefined') window.__lobbyRoom = room;
      } catch { /* ignore */ }
      // (catch-all event logger removed — kept addSubscription dedupe only)

      localSelfRef.current = {
        id: 'self',
        label: name,
        mediaStream: localStream,
        hasVideo: !videoOff,
      };

      const labelFromState = (state, participantId) => {
        try {
          const p = state?.participants?.[participantId]
            || state?.participants?.get?.(participantId);
          const ctx = p?.context;
          if (!ctx) return null;
          if (typeof ctx === 'string') {
            try {
              const parsed = JSON.parse(ctx);
              return parsed?.displayName || ctx || null;
            } catch { return ctx || null; }
          }
          if (typeof ctx === 'object') return ctx.displayName || null;
        } catch { /* ignore */ }
        return null;
      };

      const lookupParticipant = (state, pid) => {
        const participants = state?.participants;
        if (!participants) return null;
        if (typeof participants.get === 'function') return participants.get(pid) || null;
        return participants[pid] || null;
      };

      const isSelf = (participantId, state) => {
        if (localPidRef.current && participantId === localPidRef.current) return true;
        const p = lookupParticipant(state, participantId);
        if (p && (p.origin === 'local' || p.isLocal === true)) {
          if (!localPidRef.current) localPidRef.current = participantId;
          return true;
        }
        return false;
      };

      const isOwnStream = (stream) => {
        const localStream = callStreamRef.current;
        if (!stream || !localStream) return false;
        const ourIds = new Set(localStream.getTracks().map((t) => t.id));
        if (stream.audioTrack && ourIds.has(stream.audioTrack.id)) return true;
        if (stream.videoTrack && ourIds.has(stream.videoTrack.id)) return true;
        return false;
      };

      const refreshRemote = (participantId, key, state) => {
        if (isSelf(participantId, state)) return;
        const stream = room.getParticipantStream(participantId, key);
        if (!stream) return;
        if (isOwnStream(stream)) {
          if (!localPidRef.current) localPidRef.current = participantId;
          return;
        }
        const ms = new MediaStream();
        if (stream.audioTrack) ms.addTrack(stream.audioTrack);
        if (stream.videoTrack) ms.addTrack(stream.videoTrack);
        const ctxLabel = labelFromState(state, participantId);
        setRemotes((prev) => ({
          ...prev,
          [participantId]: {
            id: participantId,
            label: ctxLabel || prev[participantId]?.label || 'Participant',
            mediaStream: ms,
            hasVideo: !!stream.videoTrack && stream.isVideoEnabled,
          },
        }));
      };

      room.on('participant_joined', (participantId, state) => {
        // eslint-disable-next-line no-console
        console.log('[lobby evt] participant_joined', participantId, 'self?', isSelf(participantId, state));
        if (isSelf(participantId, state)) return;
        const ctxLabel = labelFromState(state, participantId);
        setRemotes((prev) =>
          prev[participantId]
            ? {
                ...prev,
                [participantId]: {
                  ...prev[participantId],
                  label: ctxLabel || prev[participantId].label,
                },
              }
            : {
                ...prev,
                [participantId]: {
                  id: participantId,
                  label: ctxLabel || 'Participant',
                  mediaStream: null,
                  hasVideo: false,
                },
              },
        );
      });

      room.on('participant_left', (participantId, state) => {
        if (isSelf(participantId, state)) return;
        setRemotes((prev) => {
          const next = { ...prev };
          delete next[participantId];
          return next;
        });
      });

      room.on('stream_published', async (participantId, key, state) => {
        // eslint-disable-next-line no-console
        console.log('[lobby evt] stream_published', participantId, key, 'self?', isSelf(participantId, state));
        if (isSelf(participantId, state)) return;
        try {
          await room.addSubscription(participantId, key, { audio: true, video: true });
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('addSubscription failed', participantId, key, err);
        }
      });

      room.on('stream_unpublished', (participantId, key, state) => {
        if (isSelf(participantId, state)) return;
        setRemotes((prev) => {
          const cur = prev[participantId];
          if (!cur) return prev;
          return { ...prev, [participantId]: { ...cur, mediaStream: null, hasVideo: false } };
        });
      });

      room.on('subscription_started', (participantId, key, state) => {
        // eslint-disable-next-line no-console
        console.log('[lobby evt] subscription_started', participantId, key, 'self?', isSelf(participantId, state));
        if (isSelf(participantId, state)) return;
        refreshRemote(participantId, key, state);
        // Tracks may not be attached to the SDK stream object immediately after
        // subscription_started fires. Retry so the tile populates even if the
        // initial getParticipantStream call returned null.
        setTimeout(() => {
          try {
            const r = roomRef.current;
            if (r) refreshRemote(participantId, key, r.getState?.());
          } catch { /* ignore */ }
        }, 350);
        setTimeout(() => {
          try {
            const r = roomRef.current;
            if (r) refreshRemote(participantId, key, r.getState?.());
          } catch { /* ignore */ }
        }, 1200);
      });
      room.on('subscription_reconfigured', (participantId, key, state) => {
        if (isSelf(participantId, state)) return;
        refreshRemote(participantId, key, state);
      });

      room.on('track_enabled', (participantId, key, kind, state) => {
        if (isSelf(participantId, state)) return;
        if (kind === 'video') {
          setRemotes((prev) =>
            prev[participantId]
              ? { ...prev, [participantId]: { ...prev[participantId], hasVideo: true } }
              : prev,
          );
        }
        refreshRemote(participantId, key, state);
      });
      room.on('track_disabled', (participantId, key, kind, state) => {
        if (isSelf(participantId, state)) return;
        if (kind === 'video') {
          setRemotes((prev) =>
            prev[participantId]
              ? { ...prev, [participantId]: { ...prev[participantId], hasVideo: false } }
              : prev,
          );
        }
      });

      room.on('disconnected', () => leaveRoom());

      // Reconcile remote tiles from a state snapshot. The Telnyx Video v1
      // SDK is state_changed-driven: state.participants is the source of
      // truth, and per-entity events do NOT replay for participants who
      // were already in the room when we joined.
      const reconcileFromState = async (state) => {
        try {
          const r = roomRef.current;
          if (!r || !state) return;
          // Identify self via getLocalParticipant() (preferred over guessing).
          if (!localPidRef.current) {
            try {
              const lp = r.getLocalParticipant?.();
              if (lp?.id) localPidRef.current = lp.id;
            } catch { /* ignore */ }
          }
          const partsRaw = state.participants;
          const entries = partsRaw && typeof partsRaw.entries === 'function'
            ? Array.from(partsRaw.entries())
            : Object.entries(partsRaw || {});
          const selfId = localPidRef.current;
          const seen = new Set();
          for (const [pid, p] of entries) {
            if (!pid) continue;
            if (pid === selfId) continue;
            if (p && (p.origin === 'local' || p.isLocal === true)) {
              if (!localPidRef.current) localPidRef.current = pid;
              continue;
            }
            seen.add(pid);
            // Extract displayName from context.
            let displayName = null;
            try {
              const ctx = p?.context;
              if (typeof ctx === 'string') {
                try { displayName = (JSON.parse(ctx) || {}).displayName || null; }
                catch { displayName = ctx || null; }
              } else if (ctx && typeof ctx === 'object') {
                displayName = ctx.displayName || null;
              }
            } catch { /* ignore */ }
            // Subscribe to any of their streams we haven't subscribed to yet.
            const streams = p?.streams;
            const streamEntries = streams && typeof streams.entries === 'function'
              ? Array.from(streams.entries())
              : Object.entries(streams || {});
            for (const [key /*, s */] of streamEntries) {
              const subKey = `${pid}|${key}`;
              if (!subscribedRef.current.has(subKey)) {
                subscribedRef.current.add(subKey);
                try {
                  await r.addSubscription(pid, key, { audio: true, video: true });
                } catch (err) {
                  // Already subscribed is fine; everything else log + remove from set so we can retry.
                  const msg = String(err?.message || '').toLowerCase();
                  if (!msg.includes('already')) {
                    subscribedRef.current.delete(subKey);
                    // eslint-disable-next-line no-console
                    console.warn('addSubscription via reconcile failed', pid, key, err);
                  }
                }
              }
              // If the stream is already established, render it now.
              try {
                const stream = r.getParticipantStream?.(pid, key);
                if (stream && (stream.audioTrack || stream.videoTrack)) {
                  const ms = new MediaStream();
                  if (stream.audioTrack) ms.addTrack(stream.audioTrack);
                  if (stream.videoTrack) ms.addTrack(stream.videoTrack);
                  setRemotes((prev) => ({
                    ...prev,
                    [pid]: {
                      id: pid,
                      label: displayName || prev[pid]?.label || 'Participant',
                      mediaStream: ms,
                      hasVideo: !!stream.videoTrack && stream.isVideoEnabled !== false,
                    },
                  }));
                  continue;
                }
              } catch { /* ignore */ }
            }
            // Ensure a placeholder tile exists even before the stream attaches.
            setRemotes((prev) => prev[pid] ? (
              displayName && prev[pid].label !== displayName
                ? { ...prev, [pid]: { ...prev[pid], label: displayName } }
                : prev
            ) : ({
              ...prev,
              [pid]: { id: pid, label: displayName || 'Participant', mediaStream: null, hasVideo: false },
            }));
          }
          // Drop tiles for participants who left.
          setRemotes((prev) => {
            let changed = false;
            const next = {};
            for (const [pid, tile] of Object.entries(prev)) {
              if (seen.has(pid)) next[pid] = tile;
              else changed = true;
            }
            return changed ? next : prev;
          });
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('reconcileFromState failed', err);
        }
      };

      // The state_changed event delivers a fresh state snapshot. This is
      // the primary mechanism for discovering remote participants and
      // their streams in the Telnyx Video v1 SDK.
      room.on('state_changed', (state) => {
        reconcileFromState(state);
      });

      await room.connect();

      // Capture self pid via the dedicated accessor.
      try {
        const lp = room.getLocalParticipant?.();
        if (lp?.id) localPidRef.current = lp.id;
        // eslint-disable-next-line no-console
        console.log('[lobby self] localParticipant:', lp?.id);
      } catch { /* ignore */ }

      await room.addStream('self', {
        audio: localStream.getAudioTracks()[0],
        video: localStream.getVideoTracks()[0],
      });

      if (!localPidRef.current) {
        try {
          const lp = room.getLocalParticipant?.();
          if (lp?.id) localPidRef.current = lp.id;
        } catch { /* ignore */ }
      }
      // Force one reconcile from current state in case state_changed
      // already fired before our handler was attached.
      try { reconcileFromState(room.getState?.()); } catch { /* ignore */ }
      if (localPidRef.current) {
        const selfId = localPidRef.current;
        setRemotes((prev) => {
          if (!prev[selfId]) return prev;
          const next = { ...prev };
          delete next[selfId];
          return next;
        });
      }

      // Belt-and-suspenders: re-reconcile a few times in case state_changed
      // raced with our handler registration or tracks settled after subscription.
      setTimeout(() => { try { reconcileFromState(roomRef.current?.getState?.()); } catch { /* ignore */ } }, 500);
      setTimeout(() => { try { reconcileFromState(roomRef.current?.getState?.()); } catch { /* ignore */ } }, 1500);
      setTimeout(() => { try { reconcileFromState(roomRef.current?.getState?.()); } catch { /* ignore */ } }, 3000);

      setPhase('connected');
    } catch (e) {
      setError(`Could not join room: ${e?.response?.data?.message || e?.message || e}`);
      // Restore preview ownership so the camera tile keeps showing on lobby.
      if (callStreamRef.current && !roomRef.current) {
        previewStreamRef.current = callStreamRef.current;
        callStreamRef.current = null;
        setPhase('lobby');
      } else {
        leaveRoom();
      }
    }
  }, [displayName, slug, videoOff, leaveRoom]);

  // ── In-call mute/cam toggles operate on the active call stream ────────────
  const toggleCallAudio = () => {
    const s = callStreamRef.current;
    if (!s) return;
    const next = audioMuted;
    s.getAudioTracks().forEach((t) => { t.enabled = next; });
    setAudioMuted(!next);
  };
  const toggleCallVideo = () => {
    const s = callStreamRef.current;
    if (!s) return;
    const next = videoOff;
    s.getVideoTracks().forEach((t) => { t.enabled = next; });
    setVideoOff(!next);
    if (localSelfRef.current) localSelfRef.current.hasVideo = next;
  };

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  if (phase === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white">
        <p className="text-white/60 text-sm">Loading room…</p>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white px-6">
        <div className="max-w-md w-full text-center space-y-3">
          <h1 className="text-2xl font-semibold">Can't join this call</h1>
          <p className="text-white/60 text-sm">{error}</p>
          <p className="text-white/40 text-xs">Room: <span className="font-mono">{slug}</span></p>
        </div>
      </div>
    );
  }

  if (phase === 'ended') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white px-6">
        <div className="max-w-md w-full text-center space-y-3">
          <h1 className="text-2xl font-semibold">You've left the call</h1>
          <p className="text-white/60 text-sm">You can close this tab.</p>
        </div>
      </div>
    );
  }

  // Lobby (preview + name + Join)
  if (phase === 'lobby' || phase === 'joining') {
    const joining = phase === 'joining';
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-3xl grid md:grid-cols-[2fr_1fr] gap-6 items-start">
          {/* Preview */}
          <div className="relative rounded-2xl overflow-hidden bg-black border border-white/10 aspect-video">
            <video
              ref={previewVideoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
              style={{ transform: 'scaleX(-1)' }}
            />
            {(!previewStream || videoOff) && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Avatar label={displayName || 'You'} />
              </div>
            )}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
              <button
                type="button"
                onClick={togglePreviewAudio}
                className="h-10 w-10 rounded-full bg-white/10 hover:bg-white/15 flex items-center justify-center"
                title={audioMuted ? 'Unmute' : 'Mute'}
              >
                {audioMuted ? <MicOffIcon /> : <MicIcon />}
              </button>
              <button
                type="button"
                onClick={togglePreviewVideo}
                className="h-10 w-10 rounded-full bg-white/10 hover:bg-white/15 flex items-center justify-center"
                title={videoOff ? 'Turn on camera' : 'Turn off camera'}
              >
                {videoOff ? <VideoOffIcon /> : <VideoIcon />}
              </button>
            </div>
          </div>

          {/* Info + join */}
          <div className="space-y-4">
            <div>
              <p className="text-xs uppercase tracking-wider text-white/40">You're joining</p>
              <h1 className="text-2xl font-semibold mt-1">
                {roomInfo?.title || 'Video call'}
              </h1>
              <p className="text-white/40 text-xs mt-1 font-mono">{slug}</p>
            </div>
            <div>
              <label htmlFor="displayName" className="block text-xs text-white/60 mb-1">
                Your name
              </label>
              <input
                id="displayName"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleJoin(); }}
                placeholder="e.g. Alex Lee"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/40"
                autoFocus
                disabled={joining}
              />
            </div>
            <button
              type="button"
              onClick={handleJoin}
              disabled={joining || !previewStream}
              className="w-full rounded-lg bg-sky-500 hover:bg-sky-400 disabled:bg-sky-500/40 disabled:cursor-not-allowed text-white font-semibold py-2.5 text-sm transition"
            >
              {joining ? 'Joining…' : 'Join call'}
            </button>
            {error && (
              <div className="text-rose-300 text-xs bg-rose-500/10 border border-rose-500/20 rounded-lg px-3 py-2">
                {error}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Connected — render call grid
  const remoteTiles = Object.values(remotes);
  const tiles = [localSelfRef.current, ...remoteTiles].filter(Boolean);
  return (
    <div className="min-h-screen bg-slate-950 text-white flex flex-col p-4 gap-4">
      <div className="flex items-center justify-between flex-shrink-0">
        <div>
          <p className="text-xs uppercase tracking-wider text-white/40">In call</p>
          <p className="text-lg font-semibold">{roomInfo?.title || 'Video call'}</p>
        </div>
        <p className="text-white/40 text-xs font-mono">{slug}</p>
      </div>
      <div className={`flex-1 grid gap-3 min-h-0 ${gridClasses(tiles.length)}`}>
        {tiles.map((p, i) => (
          <ParticipantTile
            key={p.id || `p-${i}`}
            id={p.id}
            label={p.label || 'Participant'}
            mediaStream={p.mediaStream}
            hasVideo={p.hasVideo}
            isLocal={p.id === 'self'}
          />
        ))}
      </div>
      <div className="flex items-center justify-center gap-3 flex-shrink-0">
        <button
          type="button"
          onClick={toggleCallAudio}
          className="h-11 w-11 rounded-full bg-white/10 hover:bg-white/15 flex items-center justify-center"
          title={audioMuted ? 'Unmute' : 'Mute'}
        >
          {audioMuted ? <MicOffIcon /> : <MicIcon />}
        </button>
        <button
          type="button"
          onClick={toggleCallVideo}
          className="h-11 w-11 rounded-full bg-white/10 hover:bg-white/15 flex items-center justify-center"
          title={videoOff ? 'Turn on camera' : 'Turn off camera'}
        >
          {videoOff ? <VideoOffIcon /> : <VideoIcon />}
        </button>
        <button
          type="button"
          onClick={leaveRoom}
          className="h-11 w-11 rounded-full bg-rose-600 hover:bg-rose-500 text-white flex items-center justify-center"
          title="Leave call"
        >
          <PhoneOffIcon />
        </button>
      </div>
    </div>
  );
}
